import React from 'react'
import { runInAction } from 'mobx'
import type { DialogItem } from 'todistukset-ui/src/vtj/stores/dialogs.mobx.store'
import { getStore } from 'todistukset-ui/src/vtj/stores/store.util'

import { GenericErrorDialogContent } from 'todistukset-ui/src/vtj/components/dialog-contents/GenericErrorDialogContent'
import { SessionTimeoutErrorDialogContent } from 'todistukset-ui/src/vtj/components/dialog-contents/SessionTimeoutErrorDialogContent'
import { SessionAboutToExpireDialogContent } from 'todistukset-ui/src/vtj/components/dialog-contents/SessionAboutToExpireDialogContent'
import { TranslationFormWithPreviousAddressesDialogContent } from 'todistukset-ui/src/vtj/components/dialog-contents/TranslationFormWithPreviousAddressesDialogContent'
import { AetRejectTodistusPyyntoDialogContent } from 'todistukset-ui/src/vtj/components/dialog-contents/AetRejectTodistuspyyntoDialogContent'

const PRIORITY_REJECT = new Error('priorityReject')

const openNextDialogInQueue = async (dialogStack: DialogItem[]) => {
  while (dialogStack.length > 0) {
    const [firstItem] = dialogStack
    try {
      let pResolve: (reason: string) => void
      let pReject: (error: Error) => void
      const promise: Promise<string> = new Promise((resolve, reject) => {
        pResolve = resolve
        pReject = reject
      })
      runInAction(() => {
        firstItem.active = {
          resolveDialog: pResolve,
          rejectDialog: pReject,
          dialogFinished: promise,
        }
        const callback = firstItem.onActivation
        firstItem.onActivation = undefined
        if (callback) {
          callback(firstItem)
        }
      })
      await promise
      runInAction(() => {
        firstItem.active = undefined
        dialogStack.shift()
      })
    } catch (e) {
      /**
       * If the dialog is force closed by a priority dialog, it will
       * stay in the queue.
       */
      runInAction(() => {
        if (firstItem.active) {
          firstItem.active = undefined
        }
        if (e !== PRIORITY_REJECT) {
          dialogStack.shift()
        }
      })
    }
  }
}

/**
 * The returned Promise<DialogItem> resolves when the Dialog is about to be shown
 * for the first time (it could be reshown later if a priority Dialog overtakes it),
 * and DialogItem.active is populated.
 */
const queueDialog = async ({
  dialogItem,
  priority = false,
}: {
  dialogItem: DialogItem
  priority?: boolean
}): Promise<DialogItem> => {
  const { dialogs: dialogStack } = getStore()
  if (
    dialogStack.filter((di: DialogItem) => di.id === dialogItem.id).length > 0
  ) {
    return Promise.reject(
      new Error(`A dialog with id ${dialogItem.id} is already in dialog queue`)
    )
  }
  runInAction(() => {
    if (priority) {
      dialogStack.unshift(dialogItem)
      dialogStack.forEach((item: DialogItem) => {
        item.active?.rejectDialog(PRIORITY_REJECT)
      })
    } else {
      dialogStack.push(dialogItem)
    }
  })

  return new Promise((resolve) => {
    const storedDialogItem = dialogStack.find(
      (di: DialogItem) => di.id === dialogItem.id
    ) as DialogItem
    storedDialogItem.onActivation = resolve
    if (dialogStack.length === 1) {
      void openNextDialogInQueue(dialogStack)
    }
  })
}

export const queuePriorityDialogGlobalError = ({
  status,
}: {
  status?: number
} = {}): Promise<DialogItem> => {
  const {
    common: { lang = 'fi' },
    dialogs: dialogStack,
  } = getStore()
  let id: string
  let component: ({
    dialogItem,
  }: {
    dialogItem: DialogItem
  }) => React.ReactElement
  let similars: Record<string, true>
  if (status === 401) {
    id = 'globalErrorSession'
    component = () => (
      <SessionTimeoutErrorDialogContent dataTestId="globalError" lang={lang} />
    )
    similars = { globalErrorSession: true }
  } else {
    id = 'globalErrorGeneric'
    component = () => <GenericErrorDialogContent dataTestId="globalError" />
    similars = { globalErrorGeneric: true, globalErrorSession: true }
  }

  const previousSimilarDialog = dialogStack.find(
    (di: DialogItem) => di.id in similars
  )
  if (previousSimilarDialog) {
    return Promise.resolve(previousSimilarDialog)
  }
  const dialogItem = { component, id }
  return queueDialog({ dialogItem, priority: true })
}

export const queuePriorityDialogSessionAboutToExpire =
  (): Promise<DialogItem> => {
    const {
      common: { sessionTimer },
    } = getStore()

    const id = 'session-about-to-expire'
    const component = ({ dialogItem }: { dialogItem: DialogItem }) => (
      <SessionAboutToExpireDialogContent
        dialogItem={dialogItem}
        sessionTimer={sessionTimer}
      />
    )

    const dialogItem = { component, id }
    return queueDialog({ dialogItem, priority: true })
  }

export const queueDialogTranslationFormWithPreviousAddresses =
  (): Promise<DialogItem> => {
    const id = 'translation-form-with-addresses'
    const component = ({ dialogItem }: { dialogItem: DialogItem }) => (
      <TranslationFormWithPreviousAddressesDialogContent
        dialogItem={dialogItem}
      />
    )

    const dialogItem = { component, id }
    return queueDialog({ dialogItem })
  }

export const queueDialogRejectAetTodistuspyynto = (
  aetTodistuspyyntoId: number
): Promise<DialogItem> => {
  const id = 'reject-aet-todistuspyynto'
  const component = ({ dialogItem }: { dialogItem: DialogItem }) => (
    <AetRejectTodistusPyyntoDialogContent
      dialogItem={dialogItem}
      aetTodistuspyyntoId={aetTodistuspyyntoId}
    />
  )

  const dialogItem = { component, id }
  return queueDialog({ dialogItem })
}
